import { Organization } from "../../../store/slices/OrganizationSlice";

// Formats an ISO status date as "June 5, 2026" to match the design copy.
// Returns null for missing or unparseable dates so callers can render the
// status pill alone instead of "Invalid Date".
export const formatStatusDate = (isoDate: string | null | undefined): string | null => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

// "123,182" — grouped number for the summary cards; falls back to a dash while
// stats are loading or unavailable.
export const formatStatValue = (value: number | null | undefined): string =>
  typeof value === "number" ? value.toLocaleString("en-US") : "–";

// The v1 parent-hierarchy endpoint returns the ancestor organizations; normalize
// to an array whether the payload is bare or wrapped in `items`.
export const normalizeHierarchy = (hierarchy: unknown): Organization[] => {
  if (Array.isArray(hierarchy)) return hierarchy;
  const items = (hierarchy as { items?: Organization[] } | null)?.items;
  return Array.isArray(items) ? items : [];
};

// Orders the ancestors of `current` root-first by walking parentOrgId links, so
// the breadcrumb does not depend on the order the API returns them in. Excludes
// `current` itself; the length guard protects against cyclic data.
export const buildAncestorChain = (
  ancestors: Organization[],
  current: { id: number; parentOrgId: number | null } | null | undefined
): Organization[] => {
  if (!current) return [];
  const byId = new Map(ancestors.map((org) => [org.id, org]));
  const chain: Organization[] = [];
  let parentId = current.parentOrgId;
  while (parentId != null && byId.has(parentId) && chain.length < byId.size) {
    const parent = byId.get(parentId) as Organization;
    chain.unshift(parent);
    parentId = parent.parentOrgId;
  }
  return chain;
};
